import { Button, Card } from "@peer-start/ui/reducate";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../app/use-app-context.js";
import calendarIcon from "../../../assets/starting-teacher/coach-calendar.svg?url";
import settingsButtonIcon from "../../../assets/starting-teacher/coach-settings-button.svg?url";
import settingsIcon from "../../../assets/starting-teacher/coach-settings.svg?url";
import { mentorMeetingPassed, type StartingTeacherProgress } from "../data/starting-teacher-route.js";

function relativeMeeting(date: Date, t: (key: string, options?: { count: number }) => string) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const meeting = new Date(date);
  meeting.setHours(12, 0, 0, 0);
  const days = Math.round((meeting.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return t("startingTeacher.mentorToday");
  if (days === 1) return t("startingTeacher.mentorInOneDay");
  if (days === -1) return t("startingTeacher.mentorOneDayAgo");
  if (days > 1) return t("startingTeacher.mentorInDays", { count: days });
  return t("startingTeacher.mentorDaysAgo", { count: Math.abs(days) });
}

function firstName(name: string | null | undefined): string | null {
  const trimmed = name?.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

const actionButtonClass =
  "!h-[36px] !min-h-[36px] !w-full !gap-[6px] !rounded-[10px] !px-[16px] !py-0 !text-[14px] !leading-[20px] !font-medium !shadow-[0_1px_2px_rgba(0,0,0,0.1)]";

/** Coach card. Notes stay closed until a coach is added. */
export function StartingTeacherMentorCard({
  progress,
  onChange,
  onAddNote,
  onPrepare,
}: {
  progress: StartingTeacherProgress;
  onChange: () => void;
  onAddNote: () => void;
  onPrepare: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { persona } = useAppContext();
  const filled = progress.hasMentor;
  const needsNextDate = filled && progress.mentorFrequency === "none" && mentorMeetingPassed(progress.mentorMeetingDate);
  const meetingDate = progress.mentorMeetingDate
    ? new Date(`${progress.mentorMeetingDate}T12:00:00`)
    : new Date();
  const locale = i18n.language === "en" ? "en-GB" : "nl-NL";
  const meetingLabel = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(meetingDate);
  const teacher = firstName(persona.name);
  const coach = firstName(progress.mentorName);
  const title = filled
    ? teacher && coach
      ? `${teacher} / ${coach}`
      : (progress.mentorName ?? t("startingTeacher.mentorTitle"))
    : t("startingTeacher.mentorEmptyTitle");

  return (
    <Card
      className="flex flex-col gap-[12px] !rounded-[12px] border !border-[rgba(0,0,0,0.1)] !p-[20px]"
      data-control-id="starting-teacher.mentor"
      padding="none"
    >
      <div className="flex w-full items-start gap-[12px]">
        <span aria-hidden="true" className="grid size-[40px] shrink-0 place-items-center rounded-[9px] bg-sidebar-accent">
          <img alt="" height={20} src={calendarIcon} width={20} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-[2px] self-stretch">
          <h2 className="text-[14px] leading-[20px] font-medium text-foreground">{title}</h2>
          <p className="text-[12px] leading-[16px] text-[#71717A]">
            {needsNextDate
              ? t("startingTeacher.mentorDateExpired")
              : filled
                ? `${meetingLabel} • ${t("startingTeacher.mentorMeetingTime")} (${relativeMeeting(meetingDate, t)})`
                : t("startingTeacher.mentorEmptyStatus")}
          </p>
        </div>
        {filled ? (
          <button
            aria-label={t("startingTeacher.coachSettings")}
            className="shrink-0"
            data-control-id="starting-teacher.mentor.change"
            onClick={onChange}
            type="button"
          >
            <img alt="" height={20} src={settingsIcon} width={20} />
          </button>
        ) : null}
      </div>
      <p className="text-[14px] leading-[20px] text-secondary-text">{t("startingTeacher.mentorNotesBody")}</p>
      <div className="flex w-full flex-col gap-[6px]">
        {filled ? (
          <Button className={actionButtonClass} controlId="starting-teacher.mentor.note" onClick={onAddNote} type="button">
            {t("startingTeacher.addNote")}
          </Button>
        ) : null}
        {needsNextDate ? (
          <Button
            className={`${actionButtonClass} !border-[rgba(0,0,0,0.1)] !bg-white !text-foreground hover:!bg-white`}
            controlId="starting-teacher.mentor.reschedule"
            onClick={onChange}
            type="button"
            variant="secondary"
          >
            {t("startingTeacher.mentorNextDate")}
          </Button>
        ) : filled ? (
          <Button
            className={`${actionButtonClass} !border-[rgba(0,0,0,0.1)] !bg-white !text-foreground hover:!bg-white`}
            controlId="starting-teacher.mentor.prepare"
            onClick={onPrepare}
            type="button"
            variant="secondary"
          >
            {t("startingTeacher.prepareMeeting")}
          </Button>
        ) : (
          <Button
            className={`${actionButtonClass} !border-[rgba(0,0,0,0.1)] !bg-white !text-foreground hover:!bg-white`}
            controlId="starting-teacher.mentor.add"
            onClick={onChange}
            type="button"
            variant="secondary"
          >
            <img alt="" height={16} src={settingsButtonIcon} width={16} />
            {t("startingTeacher.coachSettings")}
          </Button>
        )}
      </div>
    </Card>
  );
}
